import React, { useState, useEffect } from "react";
import { GingaIcon } from "@stonelog/stonelog-ginga-icons";

import arrowUpDown from "@assets/icons/arrow-up-down.svg";
import arrowLeftRight from "@assets/icons/arrow-left-right.svg";
import arrowCurveLeft from "@assets/icons/arrow-curve-left.svg";
import arrowCurveRight from "@assets/icons/arrow-curve-right.svg";
import actionDownload from "@assets/icons/action-download.svg";
import zoomIn from "@assets/icons/zoom-in.svg";
import zoomOut from "@assets/icons/zoom-out.svg";

import * as S from "./styles";

const MIME_TYPES_MAP = {
    'iVBORw0KGgo': 'image/png',
    '/9j/': 'image/jpeg',
    'R0lGOD': 'image/gif',
    'UklGR': 'image/webp'
};

const isRealImage = (base64) => Boolean(base64 && base64.length > 200);

const formatImageSrc = (base64String) => {
    if (!base64String) return "";
    const cleanString = base64String.replace(/[\s\r\n]+/g, '');
    if (cleanString.startsWith('http') || cleanString.startsWith('data:image')) {
        return cleanString;
    }
    const magicBytes = Object.keys(MIME_TYPES_MAP).find(key => cleanString.startsWith(key));
    const mimeType = magicBytes ? MIME_TYPES_MAP[magicBytes] : 'image/jpeg';
    return `data:${mimeType};base64,${cleanString}`;
};

const getExtensionFromSrc = (src) => {
    if (!src) return 'jpg';
    const match = /^data:image\/(\w+);base64,/.exec(src);
    if (!match) return 'jpg';
    const ext = match[1].toLowerCase();
    return ext === 'jpeg' ? 'jpg' : ext;
};

const downloadImage = (imgSrc, fileName) => {
    const ext = getExtensionFromSrc(imgSrc);
    const link = document.createElement('a');
    link.href = imgSrc;
    link.download = `${fileName}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const ImageGallery = ({
    images = [],
    fileName = "anexo",
    emptyMessage = "Nenhum anexo disponível."
}) => {
    const [visibleIndex, setVisibleIndex] = useState(null);
    const [scale, setScale] = useState(1);
    const [rotate, setRotate] = useState(0);
    const [flipX, setFlipX] = useState(1);
    const [flipY, setFlipY] = useState(1);

    const validImages = images.filter(isRealImage).map(formatImageSrc);
    const isOpen = visibleIndex !== null;
    const total = validImages.length;
    const currentSrc = isOpen ? validImages[visibleIndex] : null;

    const resetTransforms = () => {
        setScale(1);
        setRotate(0);
        setFlipX(1);
        setFlipY(1);
    };

    const close = () => {
        setVisibleIndex(null);
        resetTransforms();
    };

    const goPrev = (e) => {
        e?.stopPropagation();
        if (visibleIndex > 0) {
            setVisibleIndex(visibleIndex - 1);
            resetTransforms();
        }
    };

    const goNext = (e) => {
        e?.stopPropagation();
        if (visibleIndex < total - 1) {
            setVisibleIndex(visibleIndex + 1);
            resetTransforms();
        }
    };

    const handleDownloadCurrent = () => {
        if (!currentSrc) return;
        const name = `${fileName}-foto-${visibleIndex + 1}`;
        downloadImage(currentSrc, name);
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleKey = (e) => {
            if (e.key === 'Escape') close();
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'ArrowRight') goNext();
        };

        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, visibleIndex]);

    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [isOpen]);

    const transformStyle = {
        transform: `scale(${scale * flipX}, ${scale * flipY}) rotate(${rotate}deg)`,
        transition: 'transform 0.2s',
    };

    if (validImages.length === 0) {
        return (
            <S.GalleryContainer>
                <S.EmptyText>{emptyMessage}</S.EmptyText>
            </S.GalleryContainer>
        );
    }

    return (
        <>
            <S.GalleryContainer>
                {validImages.map((imgSrc, index) => (
                    <S.ImageWrapper
                        key={index}
                        onClick={() => {
                            resetTransforms();
                            setVisibleIndex(index);
                        }}
                    >
                        <S.Thumbnail src={imgSrc} alt={`Anexo ${index + 1}`} />
                    </S.ImageWrapper>
                ))}
            </S.GalleryContainer>

            {isOpen && (
                <S.PreviewOverlay onClick={close}>
                    <S.PreviewCloseButton onClick={close} title="Fechar">
                        <GingaIcon name="close" color="#FFFFFF" size={24} />
                    </S.PreviewCloseButton>

                    <S.PreviewCounter>
                        {`${visibleIndex + 1} / ${total}`}
                    </S.PreviewCounter>

                    {visibleIndex > 0 && (
                        <S.PreviewNavButton position="left" onClick={goPrev} title="Anterior">
                            <GingaIcon name="chevron-left" color="#FFFFFF" size={28} />
                        </S.PreviewNavButton>
                    )}

                    {visibleIndex < total - 1 && (
                        <S.PreviewNavButton position="right" onClick={goNext} title="Próxima">
                            <GingaIcon name="chevron-right" color="#FFFFFF" size={28} />
                        </S.PreviewNavButton>
                    )}

                    <S.PreviewImage
                        src={currentSrc}
                        alt={`Anexo ${visibleIndex + 1}`}
                        style={transformStyle}
                        onClick={(e) => e.stopPropagation()}
                    />

                    <S.PreviewToolbar onClick={(e) => e.stopPropagation()}>
                        <button type="button" onClick={handleDownloadCurrent} title="Download">
                            <img src={actionDownload} alt="download" />
                        </button>
                        <button type="button" onClick={() => setFlipY((v) => -v)} title="Espelhar vertical">
                            <img src={arrowUpDown} alt="flip vertical" />
                        </button>
                        <button type="button" onClick={() => setFlipX((v) => -v)} title="Espelhar horizontal">
                            <img src={arrowLeftRight} alt="flip horizontal" />
                        </button>
                        <button type="button" onClick={() => setRotate((r) => r - 90)} title="Girar à esquerda">
                            <img src={arrowCurveLeft} alt="rotate left" />
                        </button>
                        <button type="button" onClick={() => setRotate((r) => r + 90)} title="Girar à direita">
                            <img src={arrowCurveRight} alt="rotate right" />
                        </button>
                        <button type="button" onClick={() => setScale((s) => Math.max(0.2, s - 0.25))} title="Diminuir zoom">
                            <img src={zoomOut} alt="zoom out" />
                        </button>
                        <button type="button" onClick={() => setScale((s) => Math.min(5, s + 0.25))} title="Aumentar zoom">
                            <img src={zoomIn} alt="zoom in" />
                        </button>
                    </S.PreviewToolbar>
                </S.PreviewOverlay>
            )}
        </>
    );
};

export default ImageGallery;